import { getItem, KEYS } from 'storage';
import { getStore } from 'store';
import { setGlobal } from 'store/global/reducer';
import { DATA_NOT_FOUND, ERROR } from 'utility/errorCode';
import { ACCOUNT_ABNORMAL, ACCOUNT_DISABLED, ACCOUNT_PENDING_FOR_ACTIVATE } from 'utility/frontendMessage';
import { handleRefreshTokenException, updateIsRefreshToken, waitUntilNoTokenRefresh } from 'utility/general';
import API from './index';

const api = {
    changeRole({ roleToChange }) {
        const refreshToken = getItem(KEYS.REFRESH_TOKEN, 'refresh token is not exist in frontend');

        // prevent refresh token is running backend to solve multiple login problem.
        return waitUntilNoTokenRefresh().then((result) => {
            updateIsRefreshToken(true);

            return API.basicAPI({
                method: 'POST',
                url: '/auth/refresh',
                data: {
                    role: roleToChange,
                    refreshToken,
                },
            })
                .then((res) => {
                    // catch error when email is not exist
                    updateIsRefreshToken(false);
                    handleRefreshTokenException(res);
                    return res;
                })
                .catch((err) => {
                    updateIsRefreshToken(false);
                    throw err;
                });
        });
    },
    changeAuthUserPassword({ oldPassword, newPassword }) {
        return API.authAPI({
            url: '/user/change-auth-user-password',
            method: 'POST',
            data: {
                oldPassword,
                newPassword,
            },
        });
    },
    getUserByEmail(email, role) {
        const postData = {
            email,
        };

        if (role) {
            postData.role = role;
        }

        return API.authAPI({
            url: '/user/get-user-info',
            method: 'POST',
            data: postData,
        }).then((res) => {
            // catch error when email is not exist
            if (res.data?.status === DATA_NOT_FOUND) {
                const store = getStore();

                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Reminder',
                        errorMessage: res.data?.errorMessage,
                    }),
                );

                throw new Error(res.data?.errorMessage);
            }

            return res;
        });
    },
    getUserInfo() {
        return API.authAPI({
            url: '/user/get-my-info',
            method: 'GET',
        });
    },
    updateUserInfo(postData) {
        return API.authAPI({
            url: '/user/update-my-info',
            method: 'PATCH',
            data: postData,
        });
    },
    login({ username, password, role }) {
        return API.basicAPI({
            url: '/auth/login',
            method: 'POST',
            data: {
                username,
                password,
                role,
            },
        }).then((res) => {
            // catch error when login failed
            if (res?.data?.status === ERROR) {
                const store = getStore();

                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Login Failed',
                        errorMessage: res.data?.errorMessage,
                    }),
                );

                throw new Error(res.data?.errorMessage);
            }

            return res;
        });
    },
    signUp({ email, title, firstName, surname, institution, contact, password, confirmPassword, justification }) {
        return API.basicAPI({
            url: '/user/signup',
            method: 'POST',
            data: {
                email,
                title,
                firstName,
                surname,
                institution,
                contact,
                password,
                confirmPassword,
                justification,
            },
        });
    },
    getNews({ type }) {
        const url = type ? '/news?type=' + type : '/news';
        return API.basicAPI({
            url: url,
            method: 'GET',
        });
    },
    resendActivationEmail({ email }) {
        return API.basicAPI({
            url: '/user/resend-activation-email',
            method: 'POST',
            data: { email },
        }).then((res) => {
            // catch error when email is not exist
            if (res.data?.status === DATA_NOT_FOUND) {
                const store = getStore();

                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Email not exist',
                        errorMessage: res.data?.errorMessage,
                    }),
                );

                throw new Error(res.data?.errorMessage);
            }

            return res;
        });
    },
    forgetPassword({ email }) {
        return API.basicAPI({
            url: '/user/forget-password',
            method: 'POST',
            data: { email },
        }).then((res) => {
            // catch error when email is not exist
            const store = getStore();
            if (res.data?.status === DATA_NOT_FOUND) {
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Email not exist',
                        errorMessage: res.data?.errorMessage,
                    }),
                );

                throw new Error(res.data?.errorMessage);
            } else if (res?.data?.errorMessage === '1') {
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Reminder',
                        errorMessage: ACCOUNT_PENDING_FOR_ACTIVATE,
                    }),
                );
                throw new Error(ACCOUNT_PENDING_FOR_ACTIVATE);
            } else if (res?.data?.errorMessage === '3') {
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Reminder',
                        errorMessage: ACCOUNT_DISABLED,
                    }),
                );
                throw new Error(ACCOUNT_DISABLED);
            } else if (res?.data?.status === 'ERROR') {
                store.dispatch(
                    setGlobal({
                        isErrorShow: true,
                        errorTitle: 'Reminder',
                        errorMessage: ACCOUNT_ABNORMAL,
                    }),
                );
                throw new Error(ACCOUNT_ABNORMAL);
            }

            return res;
        });
    },
    accountActivation({ token }) {
        return API.basicAPI({
            url: '/user/account-activation',
            method: 'POST',
            data: { token },
        });
    },
    resetPassword({ token, password, confirmPassword }) {
        return API.basicAPI({
            url: '/user/reset-password',
            method: 'POST',
            data: { token, password, confirmPassword },
        });
    },
    changeFirstPassword({ passwordToken, password, confirmPassword }) {
        // const accessToken = getItem(KEYS.ACCESS_TOKEN);

        // refresh token first to prevent 401 problem when idle for 30s before submitting
        return API.handleTokenRefresh()
            .then((res) => {
                const { token } = res.data.result;

                return API.basicAPI({
                    url: '/user/change-first-password',
                    method: 'POST',
                    data: { token, passwordToken, password, confirmPassword },
                });
            })
            .catch((err) => {
                console.log('err', err);
            });
    },
    getPendingApprovalUser() {
        return API.authAPI({
            url: '/user/get-pending-approval-user',
            method: 'GET',
        });
    },
    approveEmailAccount({ id, decision, email, reason, workflowId }) {
        return API.authAPI({
            url: `/user/approve-email-account/${id}`,
            method: 'PATCH',
            data: { decision, email, reason, workflowId },
        });
    },
    logoutUser(data) {
        return API.authAPI({
            url: '/auth/logout',
            method: 'POST',
            data,
        });
    },
};

export default api;
